import { createSelector, createSlice } from "@reduxjs/toolkit";
import { logout } from "../common/reducers/initContext";
import { dataHookFactory, initMapFactory, DefaultFailureAction, DefaultSuccessAction } from "../common/reducers/utilities";
import { ensureArray, groupBy } from "../common/utils/arrayUtilities";
import { markFetched, registerEntityBasedFetch } from "../common/hooks/initHook";

const key = doc => doc.id;

export const eventDocumentSlice = createSlice({
    name: 'eventDocs',
    initialState: {
        data: {},
    },
    reducers: {
        set: (state, action) => {
            state.data[action.payload.eventid] = action.payload.data; 
        },
        addOrUpdate: (state, action) => {
            const toUpdate = ensureArray(action.payload);
            const byEvent = groupBy(toUpdate, 'eventid');
            Object.entries(byEvent).forEach(([ eventid, docs ]) => {
                const ids = new Set(docs.map(d => key(d)));
                state.data[eventid] = (state.data[eventid]?.filter(d => !ids.has(key(d))) ?? []).concat(docs);
            });
        },
        remove: (state, action) => {
            const toRemove = ensureArray(action.payload);
            const byEvent = groupBy(toRemove, 'eventid');
            Object.entries(byEvent).forEach(([ eventid, docs ]) => {
                if (state.data[eventid]) {
                    const ids = new Set(docs.map(d => key(d)));
                    state.data[eventid] = state.data[eventid].filter(d => !ids.has(key(d)));
                    if (state.data[eventid].length === 0) {
                        delete state.data[eventid];
                    }
                }
            })
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => eventDocumentSlice.getInitialState());
    }
});

// #region network operations
const visualizeSuccess = (verb, action) => DefaultSuccessAction(verb, 'eventDocuments', action);
const visualizeFailure = verb => DefaultFailureAction(verb, 'eventDocuments');

const fetchEventDocs = eventid => network => network.get('/api/v1/eventDocs/' + encodeURIComponent(eventid), data => eventDocumentSlice.actions.set({eventid, data}), visualizeFailure('get'));
export const fetchEventDoc = async (network, id) => network.fetchData('/api/v1/eventDoc/' + encodeURIComponent(id));
export const addEventDoc = (network, doc, editMode) => {
    let data = new FormData();
    data.append('data[eventid]', doc.eventid)
    data.append('data[file]', doc.file);
    data.append('data[filename]', doc.filename);
    data.append('data[member_accessible]', doc.member_accessible);
    if (editMode) {
        data.append('editMode', editMode);
    }
    return network.postData('/api/v1/eventDoc', data)
};
export const updateEventDoc = (network, data, editMode) => network.put('/api/v1/eventDoc', {editMode, data}, eventDocumentSlice.addOrUpdate, visualizeFailure('update'));
export const removeEventDoc = (network, id, editMode) => network.delete(`/api/v1/eventDoc/${encodeURIComponent(id)}${editMode ? '/' + encodeURIComponent(editMode) : ''}`, eventDocumentSlice.actions.remove, visualizeFailure('remove'));

// #endregion

// #region selectors
const selectAllEventDocData = state => state.eventDocs.data;
const selectEventDocs = eventid => createSelector([selectAllEventDocData], docs => docs[eventid] ?? []);
// #endregion

const listen = {
    eventDocCreated: (doc, { dispatch, websocket }) => {
        markFetched(websocket, 'event_doc', key(doc))
        dispatch(eventDocumentSlice.actions.addOrUpdate(doc))
    },
    eventDocRemoved: (doc, { dispatch }) => dispatch(eventDocumentSlice.actions.remove(doc))
}

const eventInitDocsByID = initMapFactory(fetchEventDocs, listen)
registerEntityBasedFetch('event', eventInitDocsByID)
const cleanup = { root: eventDocumentSlice.name, all: state => Object.values(selectAllEventDocData(state)).flat(), removeAll: eventDocumentSlice.actions.remove, key };
export const [useEventDocs, realizeEventDocs] = dataHookFactory(selectEventDocs, eventInitDocsByID, {cleanup});


const fullAccessRoles = ['administrator', 'event manager', 'template manager'];
const eventDocsReadRoles = ['auditor', 'event auditor', 'rostering manager', 'rostering auditor', 'recurring rostering manager', 'recurring rostering auditor', 'equipment manager', 'equipment auditor', 'template auditor'];

const checkPermissions = (credentials, event, requiredRoles, condition = true) => {
    return (
        credentials.hasRole(requiredRoles, { office: event?.office, client: event?.client, location: event?.locationid }) &&
        condition
    );
};

export const canViewEventDocs = (credentials, event) => checkPermissions(credentials, event, fullAccessRoles.concat(eventDocsReadRoles));
export const canUpdateEventDocs = (credentials, event) => checkPermissions(credentials, event, fullAccessRoles);
export const canRemoveEventDocs = canUpdateEventDocs;
export const canCreateEventDocs = canUpdateEventDocs;

const reducer = { name: eventDocumentSlice.name, reducer: eventDocumentSlice.reducer }
export default reducer;